<template>
  <div
    class="form-render-page"
    :class="document?.properties?.extraClass ? document?.properties?.extraClass : ''"
    :style="document?.properties?.style || ''"
  >
    <div
      v-if="document"
      class="m-0 w-full form-render-container"
      :class="{ 'p-0': isOpenInModal, card: !hideCardWrapper && !document?.properties?.hideCardWrapper }"
    >
      <div
        v-if="!document?.properties?.hidePageTitle && !isOpenInModal && showFormTitle"
        class="flex align-items-center justify-content-between"
        id="form-title"
      >
        <h5>{{ translate(document, 'properties.pageTitle', $i18n.locale) || translate(document, 'title', $i18n.locale) }}</h5>
      </div>

      <div class="document-render">
        <template v-for="element in document.documentDetails">
          <RenderElement
            :data="data"
            :aux-data="auxData"
            :element="element"
            :form-instance="formInstance"
            :is-edit-mode="false"
            :is-local-edit="isLocalEdit"
            :module="moduleId"
            :isGlobalValid="isValid"
            :isGlobalValidating="isValidating"
            @action="onAction"
          />
        </template>
      </div>
    </div>
  </div>

  <SyncModal
    v-if="showFormInModal && modalDocument"
    @close="closeModalDocument(modalDocument)"
    :size="
      modalDocument?.properties?.modalSize && modalDocument?.properties?.modalSize !== 'fullscreen'
        ? modalDocument?.properties?.modalSize
        : `medium`
    "
    :is-full-screen="modalDocument?.properties?.modalSize && modalDocument?.properties?.modalSize === 'fullscreen'"
    hide-footer
  >
    <template #header>
      <div v-if="!modalDocument?.properties?.hidePageTitle" class="flex align-items-center justify-content-between">
        <h5>
          {{ translate(modalDocument, 'properties.pageTitle', $i18n.locale) || translate(modalDocument, 'title', $i18n.locale) }}
        </h5>
      </div>
    </template>
    <template #content>
      <FormRenderPage
        :is-open-in-modal="true"
        :is-root-document="false"
        :is-local-edit="isLocalEdit"
        :form-id="modalDocument.documentId"
        :form-type-id="modalDocument.documentTypeId"
        :modal-form="modalDocument"
        :module="moduleId"
        :document-data="modalData"
        :extra-data="modalExtraData"
        @action="onModalAction"
        :parent-page-title="currentPageTitle"
        :dStage="data.dStage"
        :hide-card-wrapper="true"
        :map-controller="mapController"
      />
    </template>
  </SyncModal>

  <teleport v-if="!showFormInModal && !hideLoading && globalLoader" to="body">
    <Loader :is-loading="isLoading || isLocalLoading"/>
    <!--Loading v-model:active="isLoading" :is-full-page="true" loader="bars" style="z-index: 1000000;"/-->
  </teleport>

  <ConfirmDialog group="formConfirm" v-if="isRootDocument">
    <template #message="slotProps">
      <div v-html="`${slotProps.message.message}`" class="form-confirm-message"></div>
    </template>
  </ConfirmDialog>

  <component is="style" lang="scss" v-if="document?.properties?.localCSS">
    {{ document.properties.localCSS }}
  </component>
</template>

<script lang="ts">
import {arrayMapRecursive, clearMissingGlobalComponents, executeActions, getTemplateVariable, mapFormDataBySchema, objectVariable,} from '@/js/document/document';
import Loading from 'vue3-loading-overlay';
import user from '@/mixins/user';
import useVuelidate from '@vuelidate/core';
import {validationOptions} from '@/components/document/properties/common/validations';
import {config} from '@/config/config';
import {computed, PropType} from 'vue';
import {MapController} from '@/js/Map/map-controller';
import {MapEvent} from '@/js/document/model/map';
import {mapGetters} from 'vuex';
import {flatTree, getSurfaceElementsByPropertyValue} from '@/js/utils';
import {translate} from '@/js/translate';
import {GeometryUtil} from '@/js/Map/geometry';
import Loader from '@/components/common/Loader.vue';
import EventBus from '@/js/event-bus';
import {v4 as uuid} from "uuid";
import {MapSettingsRegistry} from '@/js/Map/registry';
import {VueComponentControl} from '@/js/Map/control/VueComponentControl';
import _ from "lodash";

export default {
  name: 'FormRenderPage',
  inject: ['dialogRef'],
  mixins: [user],
  components: {Loader, Loading},
  props: {
    skipDataDocAssign: {
      type: Boolean,
      default: false
    },
    hideCardWrapper: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: Number,
      required: false,
    },
    formTypeId: {
      type: Number,
      required: false,
    },
    modalForm: {
      type: Object,
      required: false,
    },
    forceFetchFormData: {
      type: Boolean,
      default: false,
    },
    isRootDocument: {
      type: Boolean,
      default: true,
    },
    isOpenInModal: {
      type: Boolean,
      default: false,
    },
    isMapControl: {
      type: Boolean,
      default: false,
    },
    mapController: {
      type: Object as PropType<MapController>,
    },
    documentData: {
      type: Object,
      required: false,
    },
    extraData: {
      type: Object,
      default: function () {
        return {};
      },
    },
    module: {
      type: Number,
      required: false,
    },
    isLocalEdit: {
      type: Boolean,
      default: false,
    },
    showFormTitle: {
      type: Boolean,
      default: true,
    },
    zIndexFrom: {
      type: Number,
      default: 100,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    parentPageTitle: {
      type: String,
    },
    dStage: {
      type: Number,
    },
    hideLoading: {
      type: Boolean,
      default: false,
    },
    frpModalContainer: {
      type: String,
    },
  },
  computed: {
    ...mapGetters({
      currentGlobalComponents: 'GET_CURRENT_GLOBAL_COMPONENTS',
      common: 'GET_COMMON_DATA',
      currentStages: 'GET_CURRENT_STAGES',
      currentFlows: 'GET_CURRENT_FLOWS',
      stateProject: 'GET_CURRENT_PROJECT'
    }),
    appType() {
      return config.APP_TYPE;
    },
    defaultPageTitle() {
      return config.APP_NAME + ' ' + config.APP_VERSION;
    },
    utils() {
      return {
        geometryUtil: GeometryUtil,
        flatTree: flatTree,
        mapSettingsRegistry: MapSettingsRegistry,
        vueComponentControl: VueComponentControl,
        mapEvent: MapEvent
      };
    },
  },
  data() {
    return {
      document: null,
      data: {},
      modalData: {},
      isValid: true,
      isValidating: false,
      isLoading: false,
      isLocalLoading: false,
      showFormInModal: false,
      modalDocument: null,
      modalExtraData: {},
      moduleId: null,
      onEvent: null,
      auxData: {
        reload: null,
      },
      constants: {},
      validationOptions,
      formInstance: this,
      disabledGlobalVariables: [],
      disabledGlobalConstants: [],
      currentPageTitle: null,
      scrollEl: null,
      loadingTimeout: null,
      customEventHandlers: {},
      eventBusReload: false,
      globalLoader: true
    };
  },
  async beforeMount() {
    this.$store.dispatch('setCurrentGlobalComponents', []);
    if (this.isRootDocument) {
      this.$store.dispatch('setCurrentStages', {});
    }

    if (this.forceFetchFormData || !this.isOpenInModal || this.editMode) {
      if (this.formId) {
        this.document = await this.getForm(this.formId);
      } else if (this.formTypeId) {
        this.document = await this.getForm(null, this.formTypeId);
      } else if (this.$route.params.formId) {
        this.document = await this.getForm(null, parseInt(String(this.$route.params.formId)));
      } else if (this.documentData) {
        const newDoc = this.formatDocDetails(await this.formatDoc(this.documentData));
        this.document = JSON.parse(JSON.stringify(newDoc));
      }
    } else {
      this.document = await this.getForm(this.formId, this.formTypeId, true);
    }

    if (this.module) {
      this.moduleId = this.module;
    } else if (this.$route.meta.moduleId) {
      this.moduleId = this.$route.meta.moduleId;
    }

    if (!this.moduleId) {
      const module = this.common.modules?.find(
        (m) => parseInt(m?.properties?.formId) === parseInt(this.$route.params.formId) && (!this.$route.params?.slug || this.$route.params.slug === _.kebabCase(m.title))
      );
      if (module) {
        this.moduleId = module.moduleId;
      }
    }

    if (this.documentData && !this.skipDataDocAssign) {
      this.data = JSON.parse(JSON.stringify(this.documentData));
    }

    if (this.user) {
      this.auxData = {...this.auxData, user: this.user, appType: config.APP_TYPE};
    }

    this.auxData = {...this.extraData, ...(this.modalExtraData ? this.modalExtraData : {}), ...this.auxData};

    if (this.isOpenInModal && this.dialogRef && !this.dialogRef?.options?.props?.header) {
      this.dialogRef.options.props.header = this.document?.properties?.pageTitle || this.document?.title;
    }

    this.getDocStage();

    if (this.document) {
      this.document.properties.variables = this.document.properties.variables?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
      this.document.properties.events = this.document.properties.events?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
      this.document.properties.constants = this.document.properties.constants?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
      this.document.properties.watchers = this.document.properties.watchers?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
      this.document.properties.sockets = this.document.properties.sockets?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
      this.document.properties.computed = this.document.properties.computed?.filter(
        (ev) => !ev.documentDetailId || typeof ev.documentDetailId !== 'string'
      );
    }

    this.onDocumentActions('load');

    if (this.document?.properties?.watchers) {
      this.document.properties.watchers.forEach((watcher) => {
        let varPath = getTemplateVariable(watcher.variable);
        if (!/^(data\.|auxData\.|extraData\.)/.test(varPath)) varPath = `data.${varPath}`;
        try {
          if (/runScript|stopOrContinue|ifElse/.test(watcher?.action?.code) && watcher.value) {
            this.$watch(varPath, eval(watcher.value), {deep: true});
          } else if (watcher?.action?.code) {
            this.$watch(
              varPath,
              () => {
                if (/openModal/.test(watcher?.action?.code)) {
                  this.onAction('openFormInModal', watcher, this.data);
                } else {
                  executeActions(this, [watcher], this.data, null, null, this.formInstance);
                }
              },
              {deep: true}
            );
          } else if (watcher.script) {
            this.$watch(varPath, eval(watcher.script), {deep: true});
          }
        } catch (err) {
          this.$toast.add({
            severity: 'error',
            summary: 'Watcher Error',
            detail: err,
            life: 5000,
          });
        }
      });
    }

    // subscribe to socket events
    if (this.document?.properties?.sockets) {
      this.document.properties.sockets.forEach((socket) => {
        this.sockets.subscribe(socket.variable, (data) => {
          if (/openModal/.test(socket?.action?.code)) {
            this.onAction('openFormInModal', socket, data);
          } else {
            executeActions(this, [socket], data, null, null, this.formInstance);
          }
          this.$emitter.emit('socket-message');
        });
      });
    }

    if (this.document?.properties?.computed) {
      this.auxData.computed = {};
      this.document.properties.computed.forEach((comp) => {
        this.auxData.computed[comp.name] = computed(() => eval(comp.value));
      });
    }
    this.onDocumentActions('mounted');
    if (this.document?.properties?.events) {
      this.document.properties.events
        .filter((ev) => ev?.event?.value === 'custom' && ev?.event?.eventName)
        .forEach((ev) => {
          const customEventHandler = (data) => this.handleCustomEvent(data, ev);
          EventBus.on(ev.event.eventName, customEventHandler);
          this.customEventHandlers[ev.event.eventName] = customEventHandler;
        });
    }


    EventBus.on('reload', () => {
      if (!this.data.eventBusReload) {
        this.data.eventBusReload = true;
        this.auxData.reload = Math.random();

        setTimeout(() => {
          this.data.eventBusReload = false;
        }, 2000);
      }
    });

    this.$emit('update:frpModalContainer', this.document?.properties?.modalSize);
  },
  mounted() {
    window.addEventListener('message', this.processMessage);
    this.auxData.reload = Math.random();
    if (this.extraData && this.extraData.action) {
      if (this.extraData.action.properties.onEventVariableSchema) {
        this.data = this.setModalData(this.extraData.action.properties.onEventVariableSchema, this.extraData);
      }
    }

    //add form extra class to the <main> for better styling
    let waitForDoc = setInterval(() => {
      if (this.document) {
        clearInterval(waitForDoc);
        if (this.document?.properties?.extraClass) {
          this.document?.properties?.extraClass.split(' ').forEach((el) => {
            document.querySelector('.main.main-app-container')?.classList?.add(el);
          });
        }
      }
    }, 300);

    this.scrollEl = document.getElementById('app');
    this.scrollEl.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    this.onDocumentActions('beforeDestroy');
    if (this.document?.properties?.events) {
      this.document.properties.events
        .filter((ev) => ev?.event?.value === 'custom' && ev?.event?.eventName)
        .forEach((ev) => {
          EventBus.off(ev.event.eventName, this.customEventHandlers[ev.event.eventName]);
        });
    }
    if (this.parentPageTitle) window.document.title = this.parentPageTitle;
    window.removeEventListener('message', this.processMessage);

    //remove form extra class from <main>
    if (this.document?.properties?.extraClass) {
      this.document?.properties?.extraClass.split(' ').forEach((el) => {
        document.querySelector('main.container')?.classList?.remove(el);
      });
    }

    this.scrollEl.removeEventListener('scroll', this.handleScroll);
    const appBarCenter = document.querySelector('.app-bar-center');
    if (appBarCenter) {
      appBarCenter.innerHTML = '';
    }

    // unsubscribe from socket events
    if (this.document?.properties?.sockets) {
      this.document.properties.sockets.forEach((socket) => {
        this.sockets.unsubscribe(socket.variable);
      });
    }

    clearTimeout(this.loadingTimeout);

    EventBus.off('reload');
  },
  methods: {
    getDocStage() {
      let documentStage;
      if (this.data.flowId && this.currentFlows?.length) {
        const currentFlow = this.currentFlows.find(cf => cf.documentType.documentTypeId === this.data.flowId);
        if (currentFlow) {
          if (this.data.flowStatusId) {
            documentStage = [currentFlow.documentTasks.find(dt => dt.flowStatusId === this.data.flowStatusId)];
          } else {
            documentStage = [currentFlow.documentTasks.find(dt => dt.properties?.type === 'Start')];
          }
        }
      }

      if(!documentStage) documentStage = this.data?.documentStage;

      if (documentStage && documentStage[0]) {
        const auxCurrentStages = { ...this.currentStages };
        auxCurrentStages[documentStage[0].documentId] = documentStage[0];
        this.data.dStage = documentStage[0].documentId;
        this.$store.dispatch('setCurrentStages', auxCurrentStages);
        //delete this.data.documentStage;
      } else if (this.document?.documentStage?.length) {
        const auxCurrentStages = { ...this.currentStages };
        auxCurrentStages[this.document.documentStage[0].referenceDocumentId] = this.document.documentStage[0];
        this.data.dStage = this.document.documentStage[0].referenceDocumentId;
        this.$store.dispatch('setCurrentStages', auxCurrentStages);
      } else if (this.dStage) {
        this.data.dStage = this.dStage;
      }
    },
    translate,
    generateUUID(){
      return uuid();
    },
    handleCustomEvent(data, listener) {
      executeActions(this, [listener], data, null, null, this.formInstance);
    },
    handleScroll() {
      const {scrollTop, scrollHeight, clientHeight} = this.scrollEl;
      const appBarCenter = document.querySelector('.app-bar-center');
      const title = document.getElementById('form-title');
      if (appBarCenter && title) {
        if (scrollTop > 100) {
          appBarCenter.innerHTML = title.innerHTML;
          title.style.display = 'none';
        } else {
          appBarCenter.innerHTML = '';
          title.style.display = 'flex';
        }
      }
    },
    async validateGlobal() {
      return await this.v$.$validate();
    },
    async formatDoc(response) {
      this.globalLoader = response?.properties?.isGlobalLoader;
      if(this.globalLoader === undefined){
        this.globalLoader = true;
      }
      if (typeof response.properties?.hasGlobalComponents === 'undefined' || response.properties?.hasGlobalComponents)
        await this.$store.dispatch('getGlobalComponents', {
          headers: {
            'x-access-token': this.mapController?.configuration?.accessToken || this.extraData?.accessToken,
          }
        });
      if (this.common?.globalComponents?.length)
        clearMissingGlobalComponents(response.documentDetails, this.common.globalComponents);
      if (response?.properties?.globalComponents?.length) {
        const components = await this.$store.dispatch('getFullGlobalComponents', {
          query: {
            moduleId: this.$route?.meta?.moduleId,
            documentIds: response.properties.globalComponents.map((gc) => gc.documentId).join(','),
          },
          headers: {
            'x-access-token': this.mapController?.configuration?.accessToken || this.extraData?.accessToken,
          }
        });
        this.$store.dispatch('setCurrentGlobalComponents', [...this.currentGlobalComponents, ...components]);
        const globalDocumentDetails = getSurfaceElementsByPropertyValue(response, 'title', 'Global');
        globalDocumentDetails.forEach((gdd) => {
          const currentGlobalComponent = JSON.parse(
            JSON.stringify(this.currentGlobalComponents.find((cgc) => cgc.documentId === gdd.properties?.formId))
          );
          let arr,
            evArr,
            wArr,
            cArr,
            sArr,
            compArr = null;
          if (currentGlobalComponent?.properties?.variables)
            arr = JSON.stringify(
              currentGlobalComponent.properties.variables.map((v) => ({
                ...v,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          if (currentGlobalComponent?.properties?.constants)
            cArr = JSON.stringify(
              currentGlobalComponent.properties.constants.map((v) => ({
                ...v,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          if (currentGlobalComponent?.properties?.events)
            evArr = JSON.stringify(
              currentGlobalComponent.properties.events.map((e) => ({
                ...e,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          if (currentGlobalComponent?.properties?.watchers)
            wArr = JSON.stringify(
              currentGlobalComponent.properties.watchers.map((w) => ({
                ...w,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          if (currentGlobalComponent?.properties?.sockets)
            sArr = JSON.stringify(
              currentGlobalComponent.properties.sockets.map((w) => ({
                ...w,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          if (currentGlobalComponent?.properties?.computed)
            compArr = JSON.stringify(
              currentGlobalComponent.properties.computed.map((c) => ({
                ...c,
                global: true,
                documentDetailId: gdd.documentDetailId,
              }))
            );
          const templatedStrings = [...gdd.properties.templatedStrings];
          currentGlobalComponent.properties.templatedStrings?.forEach((ts) => {
            if (!templatedStrings.map((tsAux) => tsAux.name).includes(ts.name)) {
              templatedStrings.push(ts);
            }
          });
          if (templatedStrings) {
            templatedStrings.forEach((ts) => {
              if (arr) arr = arr.replaceAll(`[[${ts.name}]]`, ts.value);
              if (evArr) evArr = evArr.replaceAll(`[[${ts.name}]]`, ts.value);
              if (wArr) wArr = wArr.replaceAll(`[[${ts.name}]]`, ts.value);
              if (cArr) cArr = cArr.replaceAll(`[[${ts.name}]]`, ts.value);
              if (sArr) sArr = sArr.replaceAll(`[[${ts.name}]]`, ts.value);
              if (compArr) compArr = compArr.replaceAll(`[[${ts.name}]]`, ts.value);
            });
          }

          if (arr)
            JSON.parse(arr).forEach((v) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (v.globalLabel) globalLabel += ` / ${v.globalLabel}`;
              const variableIndex = response.properties.variables
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex(
                  (rv) =>
                    rv.global &&
                    rv.name === v.name &&
                    rv.globalLabel === globalLabel &&
                    v.documentDetailId === rv.documentDetailId
                );
              if (variableIndex < 0) {
                response.properties.variables.push({...v, globalLabel});
              } else if (v.global) {
                response.properties.variables[variableIndex] = {...v, globalLabel};
              }
            });

          if (cArr)
            JSON.parse(arr).forEach((v) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (v.globalLabel) globalLabel += ` / ${v.globalLabel}`;
              const conIndex = response.properties.constants
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex(
                  (rv) =>
                    rv.global &&
                    rv.name === v.name &&
                    rv.globalLabel === globalLabel &&
                    v.documentDetailId === rv.documentDetailId
                );
              if (conIndex < 0) {
                response.properties.constants.push({...v, globalLabel});
              } else if (v.global) {
                response.properties.constants[conIndex] = {...v, globalLabel};
              }
            });
          if (sArr)
            JSON.parse(sArr).forEach((e) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (e.globalLabel) globalLabel += ` / ${e.globalLabel}`;
              const socketIndex = response.properties.sockets
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex((re) => {
                  return (
                    re.global &&
                    re.globalLabel === globalLabel &&
                    e.documentDetailId === re.documentDetailId &&
                    e.action.code === re.action.code &&
                    e.variable === re.variable
                  );
                });
              if (socketIndex < 0) {
                response.properties.sockets.push({...e, globalLabel});
              } else if (e.global) {
                response.properties.sockets[socketIndex] = {...e, globalLabel};
              }
            });
          if (evArr)
            JSON.parse(evArr).forEach((e) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (e.globalLabel) globalLabel += ` / ${e.globalLabel}`;
              const eventIndex = response.properties.events
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex((re) => {
                  return (
                    re.global &&
                    re.globalLabel === globalLabel &&
                    e.documentDetailId === re.documentDetailId &&
                    e.action.code === re.action.code &&
                    e.event.value === re.event.value
                  );
                });
              if (eventIndex < 0) {
                response.properties.events.push({...e, globalLabel});
              } else if (e.global) {
                response.properties.events[eventIndex] = {...e, globalLabel};
              }
            });
          if (wArr)
            JSON.parse(wArr).forEach((w) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (w.globalLabel) globalLabel += ` / ${w.globalLabel}`;
              const watcherIndex = response.properties.watchers
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex(
                  (rw) =>
                    rw.global &&
                    rw.variable === w.variable &&
                    w.documentDetailId === rw.documentDetailId &&
                    rw.globalLabel === w.globalLabel
                );
              if (watcherIndex < 0) {
                response.properties.watchers.push({...w, globalLabel});
              } else if (w.global) {
                response.properties.watchers[watcherIndex] = {...w, globalLabel};
              }
            });
          if (compArr)
            JSON.parse(compArr).forEach((w) => {
              let globalLabel = '';
              globalLabel += gdd.properties ? gdd.properties.label : currentGlobalComponent.title;
              if (w.globalLabel) globalLabel += ` / ${w.globalLabel}`;
              const computedIndex = response.properties.computed
                .filter((re) => typeof re.documentDetailId !== 'string')
                .findIndex(
                  (rw) =>
                    rw.global &&
                    rw.name === w.name &&
                    w.documentDetailId === rw.documentDetailId &&
                    rw.globalLabel === w.globalLabel
                );
              if (computedIndex < 0) {
                response.properties.computed.push({...w, globalLabel});
              } else if (w.global) {
                response.properties.computed[computedIndex] = {...w, globalLabel};
              }
            });
        });
        if (response.properties.disabledGlobalVariables) {
          response.properties.variables = response.properties.variables.filter(
            (v) =>
              !v.global ||
              !response.properties.disabledGlobalVariables.find((dgv) => {
                return (
                  v.name === dgv.name &&
                  v.globalLabel === dgv.globalLabel &&
                  v.documentDetailId === dgv.documentDetailId
                );
              })
          );
        }
        if (response.properties.disabledGlobalConstants) {
          response.properties.constants = response.properties.constants.filter(
            (v) =>
              !v.global ||
              !response.properties.disabledGlobalConstants.find((dgv) => {
                return (
                  v.name === dgv.name &&
                  v.globalLabel === dgv.globalLabel &&
                  v.documentDetailId === dgv.documentDetailId
                );
              })
          );
        }
        if (response.properties.disabledGlobalEvents) {
          response.properties.events = response.properties.events.filter(
            (ev) =>
              !ev.global ||
              !response.properties.disabledGlobalEvents.find((dgv) => {
                return (
                  ev.documentDetailId === dgv.documentDetailId &&
                  ev.action.code === dgv.action &&
                  ev.event.value === dgv.value &&
                  ev.globalLabel === dgv.globalLabel
                );
              })
          );
        }
        if (response.properties.disabledGlobalSockets) {
          response.properties.sockets = response.properties.sockets.filter(
            (ev) =>
              !ev.global ||
              !response.properties.disabledGlobalSockets.find(
                (dgv) =>
                  ev.action.code === dgv.action &&
                  ev.variable === dgv.variable &&
                  ev.globalLabel === dgv.globalLabel &&
                  ev.documentDetailId === dgv.documentDetailId
              )
          );
        }
        if (response.properties.disabledGlobalWatchers) {
          response.properties.watchers = response.properties.watchers.filter(
            (w) =>
              !w.global ||
              !response.properties.disabledGlobalWatchers.find((dgv) => {
                return (
                  w.variable === dgv.variable &&
                  w.globalLabel === dgv.globalLabel &&
                  w.documentDetailId === dgv.documentDetailId
                );
              })
          );
        }
        if (response.properties.disabledGlobalComputed) {
          response.properties.computed = response.properties.computed.filter(
            (v) =>
              !v.global ||
              !response.properties.disabledGlobalComputed.find((dgv) => {
                return (
                  v.name === dgv.name &&
                  v.globalLabel === dgv.globalLabel &&
                  v.documentDetailId === dgv.documentDetailId
                );
              })
          );
        }
        this.isLoading = false;
      } else this.isLoading = false;
      this.isLoading = false;
      return response;
    },
    async getForm(documentId, documentTypeId, skipLoad = false) {
      if (!skipLoad) this.isLoading = true;
      let params = {editMode: this.editMode};
      if (documentId) params.documentId = documentId;
      else if (documentTypeId) params.documentTypeId = documentTypeId;
      let document = null;
      if (!skipLoad || !this.modalForm) {
        document = await this.$store
          .dispatch('getForm', {
            query: {
              moduleId: this.$route?.meta?.moduleId,
            },
            data: params,
            headers: {
              'x-access-token': this.mapController?.configuration?.accessToken || this.extraData?.accessToken,
            }
          })
          .then(async (response) => {
            const res = await this.formatDoc(response);
            return res;
          })
          .catch((error) => {
            console.log(error);
            this.isLoading = false;
            this.$toast.add({
              severity: 'error',
              summary: 'Error',
              detail: error,
              life: 5000,
            });
          });
      } else {
        document = JSON.parse(JSON.stringify(this.modalForm));
      }
      this.currentPageTitle = window.document.title;
      if (!this.isMapControl) {
        window.document.title =
          document?.properties?.pageTitle || document?.title
            ? (document?.properties?.pageTitle || document?.title) + ' - ' + this.defaultPageTitle
            : this.defaultPageTitle;
      }
      return this.formatDocDetails(document);
    },
    formatDocDetails(doc) {
      if (doc?.documentDetails) {
        doc.documentDetails = arrayMapRecursive(doc.documentDetails, '_id', 'documentDetailId', 'random');
      }
      return doc;
    },
    async onAction(action, value, data) {
      switch (action) {
        case 'rootAction':
          this.$emit('action', 'rootAction', value);
          break;
        case 'openFormInModal':
          this.showFormInModal = false;
          if (value.value && typeof value.value === 'string') {
            try {
              value.value = eval(value.value);
            } catch (err) {
              console.log(err);
            }
          }
          const onEventVariableSchema = value?.properties?.onEventVariableSchemaIsScript
            ? value.properties.onEventVariableSchemaJSON
            : value.properties.onEventVariableSchema;

          this.onEvent = value?.properties;

          this.modalDocument = await this.getForm(null, value.value);
          if (data) {
            // set documentData prop of the modal (will set data variable in modal too)
            if (data.hasOwnProperty('rowIndex')) {
              this.modalExtraData.rowIndex = data.rowIndex;
              this.modalExtraData.parentRow = data;
            }
            if (onEventVariableSchema) {
              this.modalData = this.setModalData(onEventVariableSchema, data.data || data);
            }
          }
          if (value?.properties?.variables) {
            // set extraData prop of modal
            for (let variable of value?.properties?.variables) {
              this.setVariable(variable, {
                ...(data.data || data ? data.data || data : {}),
                ...(this.modalExtraData ? this.modalExtraData : {}),
              });
            }
          }
          this.showFormInModal = true;
          break;
        case 'emit':
          switch (value) {
            case 'close':
            case 'cancel':
              if (this.isMapControl) {
                this.mapController.mapInstance.fire(MapEvent.Tick, 'cancel', data.data || data, this.extraData);
              } else if (this.isOpenInModal) {
                if (this.dialogRef?.options) {
                  const dialogRefEvName = `on${value[0].toUpperCase() + value.slice(1, value.length)}`;
                  if (this.dialogRef.options[dialogRefEvName]) {
                    this.dialogRef.options[dialogRefEvName](data?.data || data, this.extraData);
                  }
                }
                this.$emit('action', 'cancel', data.data || data, this.extraData);
              } else {
                this.$router.back();
              }
              break;
            default:
              if (this.isMapControl) {
                //this.$emit("action", "cancel", data.data || data, this.extraData);
                this.mapController.mapInstance.fire(
                  MapEvent.MapFormAction,
                  'action',
                  value,
                  data.data || data,
                  this.extraData
                );
              } else if (this.isOpenInModal) {
                if (this.dialogRef?.options) {
                  const dialogRefEvName = `on${value[0].toUpperCase() + value.slice(1, value.length)}`;
                  if (this.dialogRef.options[dialogRefEvName]) {
                    this.dialogRef.options[dialogRefEvName](data?.data || data, this.extraData);
                  }
                }
                this.$emit('action', value, data?.data || data, this.extraData);
              }
              break;
          }
          break;
        case 'validate':
          this.isValidating = true;
          this.isValid = await this.validateGlobal();
          if (!this.isValid) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('Missing data'),
              detail: this.$t('Please fix the errors first!'),
              life: 5000,
            });
          }
          this.isValidating = false;
          break;
        case 'resetValidation':
          this.isValidating = false;
          this.isValid = true;
          this.v$.$reset();
          break;
        case 'reload':
          this.auxData.reload = Math.random();
          break;
        case 'loading':
          this.isLoading = value;
          break;
        case 'onEvent':
          this.onEvent = value.properties;
          this.updateOnEvent(value.value, data.data, this.data);
          break;
      }
    },
    async onModalAction(action, value, data) {
      //console.log('modal action', action, value, data);
      switch (action) {
        case 'rootAction':
          await executeActions(this, value.actions, value.data, value.element, value.dataToUpdate, this.formInstance);
          break;
        case 'close':
        case 'cancel':
          if (this.modalDocument?.properties?.reloadOnClose) {
            this.auxData.reload = Math.random();
          }
          this.showFormInModal = false;
          this.modalDocument = null;
          this.modalData = {};
          this.modalExtraData = {};

          window.document.title =
            this.document?.properties?.pageTitle || this.document?.title
              ? (this.document?.properties?.pageTitle || this.document?.title) + ' - ' + this.defaultPageTitle
              : this.defaultPageTitle;

          break;
        case 'save':
          this.showFormInModal = false;
          this.modalDocument = null;
          this.modalData = {};
          this.modalExtraData = {};
          if (!this.onEvent?.events?.length) this.auxData.reload = Math.random();
          break;
        case 'validate':
          this.isValid = await this.validateGlobal();
          if (!this.isValid) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('Missing data'),
              detail: this.$t('Please fix the errors first!'),
              life: 5000,
            });
            return;
          }
          break;
        case 'reload':
          this.auxData.reload = Math.random();
          break;
        case 'loading':
          this.isLoading = value;
          break;
      }
      this.updateOnEvent(action, value, data);
    },
    onDocumentActions(action) {
      // set defined constants
      if (action === 'load' && this.document?.properties?.constants?.length) {
        for (let con of this.document.properties.constants) {
          this.setConstant(con, this.auxData);
        }
      }

      // set defined variables
      if (action === 'load' && this.document?.properties?.variables?.length) {
        for (let variable of this.document.properties.variables) {
          if (/reload/.test(variable)) {
            this.setVariable(variable, this.auxData);
          } else {
            this.setVariable(variable, this);
          }
        }
      }

      if (this.document?.properties?.events?.length) {
        const events = this.document?.properties?.events?.filter((ev) => ev?.event?.value === action);
        if (events?.length) {
          executeActions(this, events, this.data, null, null, this.formInstance);
        }
      }
    },
    setVariable(variable, toData) {
      if (variable.type === 'script') {
        try {
          objectVariable(toData, variable.name, eval(variable.value));
        } catch (e) {
          try {
            eval(variable.value);
          } catch (e) {
          }
        }
      } else
        objectVariable(toData, variable.name, variable.type === 'object' ? JSON.parse(variable.value) : variable.value);
    },
    setConstant(con, toData) {
      if (con.type === 'script') {
        try {
          objectVariable(toData, con.name, eval(con.value));
        } catch (e) {
          try {
            eval(con.value);
          } catch (e) {
          }
        }
      } else objectVariable(toData, con.name, con.type === 'object' ? JSON.parse(con.value) : con.value);
    },
    processMessage(event) {
      if (!event.data || (event.data.source && /vue-devtools/.test(event.data.source))) {
        return;
      }
      /*if(event.data && typeof event.data === 'string' && JSON.parse(event.data).sendToParent){
        window.parent.postMessage(event.data, '*')
      }*/
      /*this.extraData = {
        ...(this.extraData || {}),
        ...event.data
      }*/
    },
    updateOnEvent(event, value, data) {
      let clearEvent = false;
      if (this.onEvent && this.onEvent.onEvent === event) {
        if (this.onEvent.onBeforeEventVariableSchema) {
          try {
            if (this.onEvent?.onBeforeEventVariableSchema && !/^\${/.test(this.onEvent?.onBeforeEventVariableSchema))
              value = mapFormDataBySchema(JSON.parse(this.onEvent.onBeforeEventVariableSchema), value);
            else
              value = objectVariable(
                value.data ? value : {data: value},
                this.onEvent.onBeforeEventVariableSchema,
                undefined,
                data
              );
          } catch (e) {
            value = objectVariable(
              value?.data ? value : {data: value},
              this.onEvent.onBeforeEventVariableSchema,
              undefined,
              data
            );
          }
        }
        if (this.onEvent.onEventVariable) {
          switch (this.onEvent.onVariableUpdateType) {
            case 'overwrite':
              objectVariable(this, this.onEvent.onEventVariable, value);
              break;
            case 'update':
              let variableValue = objectVariable(this, this.onEvent.onEventVariable, undefined, data) || [];
              if (
                data.hasOwnProperty('rowIndex') &&
                Array.isArray(variableValue) &&
                variableValue.length > data.rowIndex
              ) {
                // update
                variableValue[data.rowIndex] = value;
              } else if (Array.isArray(objectVariable(this, this.onEvent.onEventVariable, undefined, data))) {
                // push
                variableValue.push(value);
              } else {
                variableValue = [value];
              }
              objectVariable(this, this.onEvent.onEventVariable, variableValue, data);
              break;
          }
          clearEvent = true;
        }
      }

      if (['save', 'select'].includes(event)) {
        const loadEvents = this.onEvent?.events?.filter((ev) => ev?.event?.value === 'load');
        if (loadEvents && loadEvents.length) {
          if (data?.hasOwnProperty('rowIndex')) {
            if (!value) value = {};
            if (typeof value === 'object') {
              if (!Array.isArray(value)) {
                value.rowIndex = data.rowIndex;
                value.parentRow = data.parentRow;
              }
            }
          }
          executeActions(this, loadEvents, value, null, null, this.formInstance);
        }
      }

      if (clearEvent) {
        this.onEvent = null; // reset
        this.$forceUpdate();
      }
    },
    setModalData(variable, data) {
      const isFunction = (functionToCheck) => {
        try {
          new Function(functionToCheck);
          return true;
        } catch (e) {
          return false;
        }
      };

      if (variable === '${data}' || variable === 'data') {
        return data;
      } else if (/^(data|\$\{data)\./.test(variable)) {
        return objectVariable(data.data ? data : {data: data}, variable);
      } else if (isFunction(variable)) {
        return eval(variable);
      } else {
        return mapFormDataBySchema(JSON.parse(variable), data);
      }
    },
    closeModalDocument(form = null) {
      this.showFormInModal = false;
      this.modalDocument = null;
      this.modalData = {};
      this.modalExtraData = {};
      if (form?.properties?.reloadOnClose) {
        this.auxData.reload = Math.random();
      }
    },
  },
  setup() {
    return {
      v$: useVuelidate({
        $stopPropagation: true
      })
    };
  },
  sockets: {
    connect: () => {
      console.log('socket connected on form');
    },
  },
  updated() {
    this.$emit('updateFormInstance', this);
  },
  watch: {
    'data.flowId'() {
      this.getDocStage();
    },
    'data.flowStatusId'() {
      this.getDocStage();
    },
    'currentFlows'() {
      this.getDocStage();
    },
    '$root.$i18n.locale'() {
      this.$forceUpdate();
    },
  }
};
</script>

<style scoped></style>
